import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { FeatureFlagApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";

const apiClient = createApiClient(FeatureFlagApi);

export const useFeatureFlags = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const queryClient = useQueryClient();

  const useGetFeatureFlags = () =>
    useQuery({
      queryKey: [
        "featureFlags",
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      enabled: !!currentWorkspace,
      queryFn: () =>
        apiClient.featureFlagsControllerGetFeatureFlags({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
        }),
    });

  const useCreateOrUpdateFeatureFlags = () =>
    useMutation({
      mutationKey: ["createOrUpdateFeatureFlags"],
      mutationFn: (
        body: Parameters<
          typeof apiClient.featureFlagsControllerCreateOrUpdateFeatureFlags
        >["0"]["featureFlagUpsertCommand"]
      ) =>
        apiClient.featureFlagsControllerCreateOrUpdateFeatureFlags({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          featureFlagUpsertCommand: body,
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["featureFlags"] }),
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.invalidateQueries({ queryKey: ["order"] }),
          queryClient.invalidateQueries({ queryKey: ["orderItem"] }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]),
    });

  return {
    useGetFeatureFlags,
    useCreateOrUpdateFeatureFlags,
  };
};
